/**
 * Utility functions for handling URL manipulation in refine search
 */
import { MAKE_MODELS_SEPARATOR, MAKES_SEPARATOR, MULTI_MODELS_SEPARATOR } from './constants';

/**
 * List of search filter parameters that should be cleared from URL
 */
const SEARCH_FILTER_PARAMS = [
  'vehicleFueltype',
  'vehicleBodytype',
  'cabType',
  'vehicleCategory',
  'pair',
  'vehicleSeats',
  'vehicleMinRRP',
  'vehicleMaxRRP',
  'vehicleDriveTrain',
  'vehicleTransmission',
  'minBudget',
  'maxBudget',
  'budgetType'
];

/**
 * Clear search filter parameters from URL
 * @param {URL} url - URL object to modify
 */
export function clearSearchParamsFromUrl(url) {
  SEARCH_FILTER_PARAMS.forEach((param) => {
    url.searchParams.delete(param);
  });
}

/**
 * Format pair array for URL
 * @param {Array<{make: string, models: Array<string>}>} pair - Pair array
 * @returns {string} Formatted pair string for URL
 */
export function formatPairForUrl(pair) {
  return pair
    .map(({ make, models }) => {
      return models.length ? `${make}${MAKE_MODELS_SEPARATOR}${models.join(MULTI_MODELS_SEPARATOR)}` : make;
    })
    .join(MAKES_SEPARATOR);
}

/**
 * Merge refine-search params into a query object: drop stale filter keys from the
 * current route, then apply params (same rules as the History API path below).
 *
 * @param {Object} routeQuery - Vue Router route.query (plain object)
 * @param {Object} params - Search parameters from buildSearchParams
 * @param {Array<{make: string, models: Array<string>}>} pair
 * @returns {Object} Next query object for router.replace
 */
export function buildRefineSearchQuery(routeQuery, params, pair = []) {
  const next = { ...routeQuery };
  SEARCH_FILTER_PARAMS.forEach((p) => {
    delete next[p];
  });

  Object.keys(params).forEach((key) => {
    let value = params[key];

    if (key === 'pair') {
      if (pair.length === 0) {
        return;
      }
      value = formatPairForUrl(pair);
    }

    if (Array.isArray(value)) {
      value = value.join(',');
    }

    next[key] = value;
  });

  return next;
}

/**
 * Update URL with search parameters.
 * When `router` is passed, also calls `router.replace` so `this.$route.query` stays in
 * sync with the address bar (e.g. navigation that spreads `$route.query` keeps the right `pair`).
 *
 * @param {Object} params - Search parameters object
 * @param {Array<{make: string, models: Array<string>}>} pair - Pair array (for special formatting)
 * @param {Object|null} router - Vue Router instance with replace() and currentRoute (optional)
 * @returns {boolean} Whether URL was updated (false if type param exists)
 */
export function updateUrlWithSearchParams(params, pair = [], router = null) {
  const currentUrl = new URL(window.location.href);
  const typeParam = currentUrl.searchParams.get('type');

  // Don't update URL if type parameter exists
  if (typeParam) {
    return false;
  }

  if (router && typeof router.replace === 'function' && router.currentRoute) {
    const path = router.currentRoute.path;
    const nextQuery = buildRefineSearchQuery(router.currentRoute.query, params, pair);
    router.replace({ path, query: nextQuery }).catch((err) => {
      if (err && err.name !== 'NavigationDuplicated') {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    });
    return true;
  }

  // Clear existing search filter params
  clearSearchParamsFromUrl(currentUrl);

  // Add or update parameters
  Object.keys(params).forEach((key) => {
    let value = params[key];

    if (key === 'pair') {
      // Skip adding pair to URL if it's empty (user removed all makes)
      if (pair.length === 0) {
        return;
      }
      value = formatPairForUrl(pair);
    }

    if (Array.isArray(value)) {
      value = value.join(',');
    }

    currentUrl.searchParams.set(key, value);
  });

  // Fallback when no router (e.g. some tests): sync address bar only
  window.history.pushState({}, '', currentUrl);
  return true;
}
